import type { TourEvent } from '@/features/events';
import { formatBlockTime, formatDayLabel } from '@/features/events';
import { Section } from '@/components/layout';
import { ResponsiveImage } from '@/components/media';
import { ScriptLine } from '@/components/typography';
import { OrnamentIcon, Sparkle, type OrnamentIconName } from '@/components/ornaments';
import { Reveal } from '@/components/motion';
import { CANCUN_COPY } from '../copy';
import { cancunMedia, cancunThumbs } from '../media';
import { ProgramThumb } from '../ProgramThumb';
import { ScriptFlourish } from '../ornaments/ScriptFlourish';
import styles from './ProgramaCancun.module.css';

const TITLE_ID = 'cancun-programa';

/**
 * Ornamento de reserva de cada hito, por id de bloque. Hoy las seis miniaturas
 * tienen fotografía; esto sólo entra si algún día faltara una.
 */
const MILESTONE_ICONS: Record<string, OrnamentIconName> = {
  'cu-d2-amanecer': 'sun',
  'cu-d2-cenote': 'lotus',
  'cu-d2-camara': 'seed',
  'cu-d3-bio': 'shield',
  'cu-d3-fogata': 'spiral',
  'cu-d3-gala': 'heart',
};

export interface ProgramaCancunProps {
  event: TourEvent;
}

/**
 * S2 · PROGRAMA · CICLO VIVENCIAL EN 3 DÍAS — banda y 610-1138 del mockup.
 * (El póster rotula «2 DÍAS»: se queda viejo desde el PROGRAMA MODIFICADO del
 * 28-jul-2026. Manda el dato, ADR-006. Declarado en VISUAL_DEVIATIONS.)
 * El corazón de la página: timeline de dos columnas con rail de miniaturas
 * circulares solapadas, como una pila de monedas, y filete central con estrella.
 *
 * ⚠️ DESVIACIÓN DELIBERADA (registrada en `docs/VISUAL_DEVIATIONS.md`): el
 * mockup pinta 4 hitos en el DÍA 1 y 2 en el DÍA 2, con el RITUAL DEL FUEGO en
 * el día 1. En la agenda real (`cu-d3-fogata`, 18:30) la fogata es del DÍA 2.
 * No es una errata tipográfica: es información falsa sobre el programa. Manda
 * el dato. Al repartirse 3/3 desaparece el hueco donde el mockup colgaba el
 * claim manuscrito, así que el claim baja centrado bajo las dos columnas — se
 * conserva entero, cambia de sitio.
 *
 * ⚠️ ADICIÓN EXIGIDA POR EL BRIEF (ADR-006): el mockup no rotula NINGUNA hora, y
 * esto es un retiro que se compra sin saber a qué hora se madruga.
 * Se pinta la agenda COMPLETA con sus horas —las ocho o nueve franjas reales de
 * cada día, incluidas comidas y descansos— sin romper el rail: los seis hitos
 * conservan su medallón fotográfico y su tamaño, y el resto de la jornada va en
 * líneas discretas con su hora en oro. Una sola cronología, dos pesos.
 */
/** Dos rótulos son «el mismo» si sólo difieren en mayúsculas, tildes o puntuación. */
function mismoNombre(a: string, b: string): boolean {
  const limpiar = (t: string) =>
    t
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  return limpiar(a) === limpiar(b);
}

export function ProgramaCancun({ event }: ProgramaCancunProps) {
  const { programa } = CANCUN_COPY;
  /* SE REPARTE POR LO QUE EL DIA CONTIENE, NO POR SU INDICE. Un `slice(1)` diria
   * «el primero es la llegada» y eso es cierto hoy y mentira en cuanto el
   * cliente mueva una jornada. Una jornada completa tiene agenda —amanecer,
   * bloques, tertulia—; una llegada es una sola franja. */
  const jornadas = event.program.filter((d) => d.blocks.length > 1);
  const llegada = event.program.filter((d) => d.blocks.length === 1);
  // El claim vive entero en el dato; aquí solo se parte por frase, como el
  // mockup, sin reescribir una palabra.
  const claimLines = event.scriptClaim?.split(/(?<=\.)\s+/).filter(Boolean) ?? [];

  return (
    <Section
      background="surface"
      spacing="tight"
      labelledBy={TITLE_ID}
      className={styles.section}
      ornament={
        <>
          <span className={styles.washLeft} />
          <span className={styles.washRight} />

          {/* Flanco derecho del mockup: el acantilado con la ruina arriba y la
              hoja de palma abajo. Los dos se funden en el crema con máscara,
              nunca con un borde duro. */}
          <ResponsiveImage asset={cancunMedia.ruins} sizes="26vw" className={styles.ruins} decorative />

          <ResponsiveImage asset={cancunMedia.palmLeaf} sizes="30vw" className={styles.palm} decorative />
        </>
      }
    >
      {/* La gota de llama YA NO VIVE AQUÍ: cuelga del arco, dentro de
          `HeroCancun`. Desde esta sección no podía cruzar el filete porque
          `.section` lleva `overflow: hidden` y el margen negativo la recortaba
          en vez de subirla. El motivo largo está en `HeroCancun.tsx`. */}
      <Reveal className={styles.head}>
        <h2 id={TITLE_ID} className={styles.title}>
          <span className={styles.titleRule} />
          <span>{programa.eyebrow}</span>
          <span className={styles.titleRule} />
        </h2>

        <p className={styles.subtitle}>{programa.subtitle}</p>

        {/* EL GANCHO DE VENTA SE FUE ARRIBA (28-jul-2026). Estaba aquí, delante
         * de la agenda, porque era el único párrafo de la página que argumentaba.
         * El cliente pidió que el «por qué ir» viviera justo debajo del CTA de
         * reserva, pegado al hero, y ahí es donde lo pinta ahora `PorQueIr` —
         * verbatim y desde el mismo dato (`cancun.salesHook`).
         *
         * NO SE PINTA DOS VECES A PROPÓSITO: repetir un párrafo de 430 caracteres
         * a dos scrolls de distancia no refuerza, cansa, y además el segundo
         * desmiente al primero (si ya lo he leído, ¿por qué me lo repites?). */}
      </Reveal>

      {/* LA JORNADA DE LLEGADA NO ES UNA TERCERA COLUMNA, Y ES A PROPOSITO.
       * El «PROGRAMA MODIFICADO» del 28-jul-2026 antepone el 18 de febrero, que
       * es una sola linea: cena de presentacion a las 20:00. Meterla como
       * columna romperia la composicion del poster —la rejilla de 768+ es
       * `1fr auto 1fr`, o sea DOS columnas con el filete en medio, y una tercera
       * caeria dentro del track del filete— y ademas quedaria una columna casi
       * vacia al lado de dos jornadas de nueve franjas. Va de entradilla: el
       * dato se publica entero y el mockup no se toca (ADR-006). */}
      {llegada.map((day) => (
        <Reveal key={day.day}>
          {/* SE LEE COMO UNA JORNADA, NO COMO UNA COLETILLA. Antes iba en una sola
           * línea pequeña y centrada, pegada al párrafo de venta: el ojo saltaba
           * directo a las dos columnas grandes y el programa PARECÍA de dos días.
           * Lo reportó el cliente mirando la página publicada. Ahora lleva el
           * MISMO tratamiento de cabecera que las otras dos jornadas —aro solar,
           * «DÍA n» en versalitas y su título debajo— y se apoya sobre una banda
           * propia. Sigue sin ser una tercera columna: la rejilla del póster es de
           * dos, y meter una tercera casi vacía era el otro error posible. */}
          <section className={styles.arrival} aria-labelledby={`cancun-dia-${day.day}`}>
            <h3 id={`cancun-dia-${day.day}`} className={styles.dayHead}>
              <OrnamentIcon name="moon" size={22} className={styles.dayIcon} />
              <span>{formatDayLabel(day)}</span>
            </h3>
            <p className={styles.dayTitle}>{day.title}</p>
            <ul className={styles.arrivalList} role="list">
              {day.blocks.map((block) => (
                <li key={block.id} className={styles.arrivalBlock}>
                  <span className={styles.time}>{formatBlockTime(block)}</span>
                  {/* NO SE REPITE EL NOMBRE. En la jornada de llegada el título
                   * del día Y el del único bloque son la misma cosa —la cena de
                   * presentación—, así que pintarlos los dos deja el texto
                   * duplicado y parece un fallo. Se compara normalizado, no por
                   * igualdad exacta, porque uno va en capital y el otro no. */}
                  {mismoNombre(day.title, block.title) ? null : (
                    <span className={styles.arrivalTitle}>{block.title}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        </Reveal>
      ))}

      <div className={styles.timeline}>
        {jornadas.map((day) => (
          <section key={day.day} className={styles.column} aria-labelledby={`cancun-dia-${day.day}`}>
            <h3 id={`cancun-dia-${day.day}`} className={styles.dayHead}>
              <OrnamentIcon name="sun" size={22} className={styles.dayIcon} />
              <span>{formatDayLabel(day)}</span>
            </h3>
            {/* «DÍA 2» no dice nada. El programa del cliente titula cada jornada
             * —«Purificación y Anclaje en la Playa», «Activación de Poder y Fuego
             * Estelar»— y ese título estaba en el dato sin llegar a la página. */}
            <p className={styles.dayTitle}>{day.title}</p>

            <ol className={styles.rail} role="list">
              {day.blocks.map((block) =>
                block.isHighlight ? (
                  <li key={block.id} className={styles.milestone}>
                    <ProgramThumb image={cancunThumbs[block.id]} icon={MILESTONE_ICONS[block.id] ?? 'star'} />
                    <div className={styles.milestoneBody}>
                      <p className={styles.time}>{formatBlockTime(block)}</p>
                      {/* MANDA EL NOMBRE DEL PDF, NO EL RÓTULO DEL PÓSTER. Aquí
                       * se pintaba `highlightTitle` —la etiqueta corta que dibuja
                       * el mockup: «Cenote & Purificación», «Ritual del Fuego»— y
                       * eso tapaba el nombre real del bloque en el programa del
                       * cliente. El nombre de una actividad es CONTENIDO, y en el
                       * contenido manda el brief (ADR-006). El rótulo del póster
                       * sobrevive como subtítulo, que es donde no miente. */}
                      <h4 className={styles.milestoneTitle}>{block.title}</h4>
                      {block.highlightSubtitle ? (
                        <p className={styles.milestoneSub}>{block.highlightSubtitle}</p>
                      ) : null}
                      {/* LA DESCRIPCION DEL PROGRAMA NO SE PINTABA EN NINGUN SITIO
                       * de esta pagina, y es la unica que dice QUE se hace en cada
                       * franja: el cenote sagrado, la limpieza de memorias, la
                       * palapa del taller del espejo. Estaba en el dato desde el
                       * PDF del cliente y el visitante no la veia. */}
                      {block.description ? <p className={styles.blockDesc}>{block.description}</p> : null}
                    </div>
                  </li>
                ) : (
                  <li key={block.id} className={styles.minor}>
                    <span className={styles.minorDot} aria-hidden="true" />
                    <div className={styles.milestoneBody}>
                      <p className={styles.time}>{formatBlockTime(block)}</p>
                      <h4 className={styles.minorTitle}>{block.title}</h4>
                      {block.description ? <p className={styles.blockDesc}>{block.description}</p> : null}
                    </div>
                  </li>
                ),
              )}
            </ol>
          </section>
        ))}

        <span className={styles.axis} aria-hidden="true">
          <Sparkle size={20} className={styles.axisStar} />
        </span>
      </div>

      {/* `size="md"` Y NO `"lg"`: MEDIDO EN EL PÓSTER, NO ELEGIDO.
       * `lg` daba 52,67 px de cuerpo y **354 px de trazo (34,6 % vw)** contra los
       * **203 px (19,8 %)** que mide el claim del póster — 14,8 pp, la mayor
       * desviación de tamaño que quedaba sin declarar en la página. `md` da
       * 35,6 px, y con él la altura de x cae a ~9,5 px contra los ~10 px del
       * póster: **la percepción de tamaño coincide**. El trazo se queda en
       * ~23 % vw (+3,4 pp) porque Allura es más ancha por em que la manuscrita
       * del póster, y eso ya no se arregla con el cuerpo. Va declarado en P-C3. */}
      <Reveal className={styles.claim}>
        <ScriptLine size="md" align="center" className={styles.claimText}>
          {claimLines.map((line) => (
            <span key={line} className={styles.claimLine}>
              {line}
            </span>
          ))}
        </ScriptLine>
        <ScriptFlourish className={styles.flourish} />
      </Reveal>
    </Section>
  );
}
